import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Fab,
  IconButton,
  LinearProgress,
  Pagination,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import EmailIcon from '@mui/icons-material/Email'
import LogoutIcon from '@mui/icons-material/Logout'
import RefreshIcon from '@mui/icons-material/Refresh'
import WifiOffIcon from '@mui/icons-material/WifiOff'
import CloudOffIcon from '@mui/icons-material/CloudOff'
import AppLogo from './components/AppLogo'
import UserInfoModal from './components/UserInfoModal'

// Features
import { FileList, FileViewer, useFiles, useSelection } from './features/files'
import { FolderBreadcrumb, CreateFolderDialog, MoveToFolderDialog, useFolders } from './features/folders'
import { DropZone, UploadProgress, UploadConfirmationModal, useUpload } from './features/upload'
import { AnalysisPage } from './features/analysis'
import { MultiComparisonPage } from './features/comparison'
import { ProcessingStatusPage } from './features/processing'
import { SearchModal } from './features/search'

// Shared
import { Toolbar, SelectionToolbar, EmptyState } from './shared/components/layout'
import { ErrorDisplay, MoveSuccessToast } from './shared/components/feedback'
import ConfirmDeleteDialog, { DeleteItem, DeleteItemType } from './shared/components/feedback/ConfirmDeleteDialog'
import RenameDialog from './shared/components/feedback/RenameDialog'
import { SelectionBox } from './shared/components/selection'
import { getAcceptString } from './shared/constants'
import { usePolling } from './shared/hooks'

// Contexts
import { AppProviders, useAppConfig, useBetaMode, useNPS } from './contexts'
import { useNotification } from './contexts/NotificationContext'

// Services (ainda usando o antigo por compatibilidade)
import {
  FileItem,
  deleteFile as deleteFileApi,
  moveFileToFolder,
  moveFilesToFolder,
  moveFolder as moveFolderApi,
  sendTestEmail
} from './services/api'
import type { TranscriptionFolder } from './features/folders/types/folder.types'
import type { UploadOptions } from './features/upload/types/upload.types'
import type { ViewMode, SortOption } from './shared/components/layout/Toolbar'
import type { BatchAction } from './shared/components/layout/SelectionToolbar'

// Auth
import LoginPage from './components/LoginPage'
import { needsLogin, getSession, applyAuthToConfig, clearSession } from './services/authService'
import type { AuthData } from './services/authService'

export type AppConfig = Window['monkeeTranscription']
type AppView = 'files' | 'analysis' | 'comparison' | 'processing'

function AppContent({ config }: { config?: AppConfig }) {
  const { showMessage } = useNotification()
  const { triggerNPS, setCurrentView: setNPSView } = useNPS()
  const betaMode = useBetaMode()
  const appEnv = import.meta.env.VITE_APP_ENV as string | undefined
  const isStagingEnv = appEnv === 'staging'
  const isDevEnv = appEnv === 'development'

  // ═══════════════════════════════════════════
  // Autenticação / Logout
  // ═══════════════════════════════════════════

  // Detecta se o usuário está usando autenticação standalone (não WordPress)
  const isStandaloneAuth = useMemo(() => {
    return getSession() !== null
  }, [])

  // Sessão atual (standalone) e dados do usuário
  const session = useMemo(() => getSession(), [])

  const userDisplayName = session?.displayName || config?.userEmail || session?.email
  const userEmail = config?.userEmail || session?.email
  const userId = config?.userId ?? session?.userId
  const userTennantId = config?.tennantId || session?.tennantId
  const userTags = config?.userTags ?? []

  // Estado do modal de informações do usuário
  const [showUserInfoModal, setShowUserInfoModal] = useState(false)

  // Handler de logout
  const handleLogout = useCallback(() => {
    clearSession()
    // Recarrega a página para forçar nova checagem de autenticação
    window.location.reload()
  }, [])

  // ═══════════════════════════════════════════
  // Hooks customizados
  // ═══════════════════════════════════════════

  // Files hook
  const {
    files,
    folders,
    loading,
    error,
    currentPage,
    totalPages,
    totalFiles,
    currentFolderId,
    currentFolderName,
    sortOption,
    retryCountdown,
    hasProcessing,
    filteredFiles,
    fetchFiles,
    deleteFile,
    renameFile,
    setSearchQuery,
    setSortOption,
    navigateToFolder,
    clearError,
    setFolders
  } = useFiles()

  // Selection hook
  const {
    selectedFiles,
    toggleSelection,
    selectAll,
    clearSelection,
    setSelectedByIds
  } = useSelection()

  // Upload hook
  const {
    uploadingFiles,
    pendingFiles,
    showConfirmation,
    isUploading,
    selectFiles,
    confirmUpload: handleConfirmUploadHook,
    cancelUpload,
    removePendingFile
  } = useUpload(() => fetchFiles(1, sortOption))

  // Folders hook
  const {
    showCreateDialog,
    editingFolder,
    showMoveDialog,
    movingFolder,
    openCreateDialog,
    closeCreateDialog,
    openEditDialog,
    closeEditDialog,
    openMoveDialog,
    closeMoveDialog,
    createFolder,
    renameFolder,
    deleteFolder: deleteFolderAction,
    moveFolder,
    moveFiles,
    moveToast,
    closeMoveToast
  } = useFolders(
    () => fetchFiles(currentPage, sortOption),
    (msg) => showMessage(msg)
  )

  // ═══════════════════════════════════════════
  // Estado local (UI)
  // ═══════════════════════════════════════════

  const [searchQuery, setSearchQueryLocal] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>('grid')
  const [viewerFile, setViewerFile] = useState<FileItem | null>(null)

  // Navigation state
  const [currentView, setCurrentView] = useState<AppView>('files')
  const [analysisFile, setAnalysisFile] = useState<FileItem | null>(null)
  const [processingFile, setProcessingFile] = useState<FileItem | null>(null)
  const [comparisonFiles, setComparisonFiles] = useState<FileItem[]>([])

  // Moving state
  const [movingFile, setMovingFile] = useState<FileItem | null>(null)
  const [isMovingItems, setIsMovingItems] = useState(false)

  // Search modal state
  const [showSearchModal, setShowSearchModal] = useState(false)

  // Email test state (dev only)
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false)
  const emailTestEnabled = import.meta.env.VITE_ENABLE_EMAIL_TEST === 'true'
  const testEmailAddress = config?.userEmail ?? ''

  // Delete confirmation state
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean
    itemType: DeleteItemType
    itemName?: string
    items?: DeleteItem[]
    onConfirm: () => Promise<void>
  }>({
    open: false,
    itemType: 'file',
    onConfirm: async () => {}
  })
  const [isDeleting, setIsDeleting] = useState(false)

  // Rename state
  const [renameDialog, setRenameDialog] = useState<{
    open: boolean
    file: FileItem | null
  }>({ open: false, file: null })
  const [isRenaming, setIsRenaming] = useState(false)

  // Refs
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const fileListContainerRef = useRef<HTMLDivElement>(null)
  const selectionContainerRef = useRef<HTMLDivElement>(null)
  const mouseDownPosRef = useRef<{ x: number; y: number } | null>(null)

  // ═══════════════════════════════════════════
  // Handlers de clique em área vazia
  // ═══════════════════════════════════════════

  const handleContainerMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // Registra a posição inicial do mouse
    mouseDownPosRef.current = { x: e.clientX, y: e.clientY }
  }, [])

  const handleContainerClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (selectedFiles.length === 0) return

    // Verifica se houve movimento (arraste) - se sim, não desseleciona
    if (mouseDownPosRef.current) {
      const deltaX = Math.abs(e.clientX - mouseDownPosRef.current.x)
      const deltaY = Math.abs(e.clientY - mouseDownPosRef.current.y)
      
      // Se moveu mais de 5px, foi um arraste, não um clique
      if (deltaX > 5 || deltaY > 5) {
        mouseDownPosRef.current = null
        return
      }
    }
    mouseDownPosRef.current = null

    const target = e.target as HTMLElement

    // Se o clique foi em um card/item selecionável ou dentro de um, não desseleciona
    if (target.closest('[data-select-id]')) return

    // Se o clique foi em elementos interativos, não desseleciona
    if (
      target.closest('button') ||
      target.closest('input') ||
      target.closest('a') ||
      target.closest('[role="menu"]') ||
      target.closest('[role="dialog"]') ||
      target.closest('.MuiCheckbox-root') ||
      target.closest('.MuiIconButton-root') ||
      target.closest('.MuiMenu-root') ||
      target.closest('.MuiModal-root')
    ) return

    // Clique em área vazia - desseleciona tudo
    clearSelection()
  }, [selectedFiles.length, clearSelection])

  // ═══════════════════════════════════════════
  // Polling automático
  // ═══════════════════════════════════════════

  usePolling(
    () => fetchFiles(currentPage, sortOption, true),
    error ? 10000 : 5000,
    hasProcessing || !!error
  )

  // ═══════════════════════════════════════════
  // Effects
  // ═══════════════════════════════════════════

  useEffect(() => {
    fetchFiles(1, sortOption)
  }, [])

  // Sincroniza busca local com hook
  useEffect(() => {
    setSearchQuery(searchQuery)
  }, [searchQuery, setSearchQuery])

  // Sincroniza a view atual com o NPSContext para envio contextual do feedback
  useEffect(() => {
    setNPSView(currentView)
  }, [currentView, setNPSView])

  // Dispara NPS ao abrir análise concluída
  useEffect(() => {
    if (currentView === 'analysis' && analysisFile) {
      triggerNPS('ANALYSIS_COMPLETE', analysisFile.fileId)
    }
  }, [currentView, analysisFile?.fileId])

  // Global keyboard shortcuts (Ctrl+K for search, Ctrl+A for select all)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignora se estiver em um input/textarea
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        setShowSearchModal(true)
      }

      // Ctrl+A - Selecionar todos os arquivos (apenas na view de arquivos)
      if ((e.ctrlKey || e.metaKey) && e.key === 'a' && currentView === 'files') {
        e.preventDefault()
        if (filteredFiles.length > 0) {
          selectAll(filteredFiles)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [currentView, filteredFiles, selectAll])

  // ═══════════════════════════════════════════
  // Handlers
  // ═══════════════════════════════════════════

  const handleUploadClick = () => fileInputRef.current?.click()

  const handleFilesSelected = (fileList: FileList | null) => {
    selectFiles(fileList)
  }

  const handleConfirmUpload = async (files: File[], options: UploadOptions) => {
    // Incluir folderId atual nas opções de upload
    const optionsWithFolder: UploadOptions = {
      ...options,
      folderId: currentFolderId
    }
    
    const result = await handleConfirmUploadHook(files, optionsWithFolder)
    if (result.success > 0) {
      showMessage(`${result.success} arquivo(s) enviado(s) com sucesso!${result.failed > 0 ? ` ${result.failed} falhou(am).` : ''}`)
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleDelete = (fileId: string) => {
    const file = files.find(f => f.fileId === fileId)
    setDeleteDialog({
      open: true,
      itemType: 'file',
      itemName: file?.displayName || file?.title || 'arquivo',
      onConfirm: async () => {
        setIsDeleting(true)
        try {
          const success = await deleteFile(fileId)
          if (success) {
            showMessage('Arquivo removido com sucesso')
          }
        } finally {
          setIsDeleting(false)
          setDeleteDialog(prev => ({ ...prev, open: false }))
        }
      }
    })
  }

  /**
   * Executa a exclusão diretamente sem abrir modal
   * Usado quando o modal já foi exibido (ex: ProcessingStatusPage)
   */
  const handleDeleteDirect = async (fileId: string) => {
    const success = await deleteFile(fileId)
    if (success) {
      showMessage('Arquivo removido com sucesso')
      fetchFiles(currentPage, sortOption)
    }
  }

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    fetchFiles(page, sortOption)
  }

  const handleSortChange = (sort: SortOption) => {
    setSortOption(sort)
    fetchFiles(1, sort)
  }

  const handleOpenAnalysis = (file: FileItem) => {
    if (file.status === 'completed') {
      setAnalysisFile(file)
      setCurrentView('analysis')
    } else {
      setProcessingFile(file)
      setCurrentView('processing')
    }
  }

  const handleBackToFiles = () => {
    setCurrentView('files')
    setAnalysisFile(null)
    setProcessingFile(null)
    setComparisonFiles([])
    clearSelection()
  }

  const handleSelectAll = () => {
    selectAll(filteredFiles)
  }

  const handleRubberBandSelection = useCallback((ids: string[]) => {
    setSelectedByIds(ids, files)
  }, [files, setSelectedByIds])

  const handleBatchAction = async (action: BatchAction) => {
    switch (action) {
      case 'compare':
        const completedFiles = selectedFiles.filter(f => f.status === 'completed')
        if (completedFiles.length >= 2 && completedFiles.length <= 4) {
          setComparisonFiles(completedFiles)
          setCurrentView('comparison')
          clearSelection()
        }
        break

      case 'moveToFolder':
        openMoveDialog()
        break

      case 'delete':
        setDeleteDialog({
          open: true,
          itemType: 'batch',
          items: selectedFiles.map(f => ({
            id: f.fileId,
            name: f.title,
            type: 'file' as const
          })),
          onConfirm: async () => {
            setIsDeleting(true)
            try {
              let deleted = 0
              for (const file of selectedFiles) {
                try {
                  await deleteFileApi(file.fileId)
                  deleted++
                } catch (e) {
                  console.error('Erro ao excluir', file.title)
                }
              }
              showMessage(`${deleted} arquivo(s) excluído(s) com sucesso`)
              fetchFiles(currentPage, sortOption)
              clearSelection()
            } finally {
              setIsDeleting(false)
              setDeleteDialog(prev => ({ ...prev, open: false }))
            }
          }
        })
        break
    }
  }

  // Folder handlers
  const handleNavigateToFolder = (folderId: string | null) => {
    navigateToFolder(folderId, folders.find(f => f.id === folderId)?.name)
    clearSelection()
  }

  const handleCreateFolder = async (name: string, color: string | null) => {
    const success = await createFolder(name, color, currentFolderId)
    if (success) {
      showMessage(`Pasta "${name}" criada com sucesso`)
    }
  }

  const handleRenameFolder = (folder: TranscriptionFolder) => {
    openEditDialog(folder)
  }

  const handleConfirmRenameFolder = async (name: string, color: string | null) => {
    const success = await renameFolder(name, color)
    if (success) {
      showMessage(`Pasta renomeada para "${name}"`)
    }
  }

  const handleDeleteFolder = (folder: TranscriptionFolder) => {
    setDeleteDialog({
      open: true,
      itemType: 'folder',
      itemName: folder.name,
      onConfirm: async () => {
        setIsDeleting(true)
        try {
          const success = await deleteFolderAction(folder)
          if (success) {
            showMessage(`Pasta "${folder.name}" excluída com sucesso`)
          }
        } finally {
          setIsDeleting(false)
          setDeleteDialog(prev => ({ ...prev, open: false }))
        }
      }
    })
  }

  const handleMoveFileToFolder = (file: FileItem) => {
    setMovingFile(file)
    openMoveDialog()
  }

  const handleRenameFile = (file: FileItem) => {
    setRenameDialog({ open: true, file })
  }

  const handleConfirmRenameFile = async (newName: string) => {
    if (!renameDialog.file) return
    setIsRenaming(true)
    try {
      const success = await renameFile(renameDialog.file.fileId, newName)
      if (success) {
        if (analysisFile?.fileId === renameDialog.file.fileId) {
          setAnalysisFile(prev => prev
            ? {
                ...prev,
                displayName: newName.trim() || null
              }
            : prev)
        }
        showMessage('Transcrição renomeada com sucesso')
        setRenameDialog({ open: false, file: null })
      }
    } finally {
      setIsRenaming(false)
    }
  }

  // ═══════════════════════════════════════════
  // Atalhos de teclado
  // ═══════════════════════════════════════════

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignora se estiver digitando em um input ou textarea
      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      // Delete ou Del - excluir itens selecionados
      if ((e.key === 'Delete' || e.key === 'Del') && selectedFiles.length > 0) {
        e.preventDefault()
        handleBatchAction('delete')
      }

      // Esc - limpar seleção
      if (e.key === 'Escape' && selectedFiles.length > 0) {
        e.preventDefault()
        clearSelection()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [selectedFiles])

  const handleDropItemsToFolder = async (targetFolderId: string, itemIds: string[], itemType: 'file' | 'folder') => {
    // Ativar skeleton IMEDIATAMENTE para feedback instantâneo
    setIsMovingItems(true)
    
    try {
      if (itemType === 'folder') {
        for (const id of itemIds) {
          await moveFolderApi(id, targetFolderId)
        }
      } else {
        await moveFiles(itemIds, targetFolderId)
      }
      clearSelection()
    } finally {
      // Desativar skeleton após operação (sucesso ou erro)
      setIsMovingItems(false)
    }
  }

  const handleConfirmMoveToFolder = async (targetFolderId: string | null) => {
    closeMoveDialog()

    if (movingFolder) {
      await moveFolder(movingFolder.id, targetFolderId)
    } else if (movingFile) {
      await moveFiles([movingFile.fileId], targetFolderId)
      setMovingFile(null)
    } else if (selectedFiles.length > 0) {
      await moveFiles(selectedFiles.map(f => f.fileId), targetFolderId)
      clearSelection()
    }
  }

  // ═══════════════════════════════════════════
  // Computed
  // ═══════════════════════════════════════════

  const hasConnectionError = error && (error.type === 'network' || error.type === 'server' || error.type === 'timeout')
  const isDisabled = isUploading || !!hasConnectionError

  // ═══════════════════════════════════════════
  // Views condicionais
  // ═══════════════════════════════════════════

  if (currentView === 'analysis' && analysisFile) {
    const isVideo = ['MP4', 'MOV', 'AVI', 'MKV', 'WEBM'].includes(analysisFile.type?.toUpperCase() || '')
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
        <AnalysisPage
          fileId={analysisFile.fileId}
          fileName={analysisFile.displayName || analysisFile.title}
          fileType={isVideo ? 'VIDEO' : 'AUDIO'}
          processingTimes={{
            createdAt: analysisFile.createdAt,
            processingStartedAt: analysisFile.processingStartedAt ?? null,
            transcriptionStartedAt: analysisFile.transcriptionStartedAt ?? null,
            transcriptionCompletedAt: analysisFile.transcriptionCompletedAt ?? null,
            analysisCompletedAt: analysisFile.analysisCompletedAt ?? null
          }}
          onBack={handleBackToFiles}
          onRename={() => handleRenameFile(analysisFile)}
        />

        <RenameDialog
          open={renameDialog.open}
          onClose={() => setRenameDialog({ open: false, file: null })}
          onConfirm={handleConfirmRenameFile}
          currentName={renameDialog.file?.displayName || renameDialog.file?.title || ''}
          originalName={renameDialog.file?.title || ''}
          isRenaming={isRenaming}
        />
      </Box>
    )
  }

  if (currentView === 'comparison' && comparisonFiles.length >= 2) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
        <MultiComparisonPage
          files={comparisonFiles}
          onBack={handleBackToFiles}
        />
      </Box>
    )
  }

  if (currentView === 'processing' && processingFile) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
        <ProcessingStatusPage
          file={processingFile}
          onBack={handleBackToFiles}
          onRefresh={() => fetchFiles(currentPage, sortOption, true)}
          onDelete={handleDeleteDirect}
          onOpenAnalysis={() => {
            setAnalysisFile(processingFile)
            setCurrentView('analysis')
          }}
        />
      </Box>
    )
  }

  // ═══════════════════════════════════════════
  // Render principal
  // ═══════════════════════════════════════════

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      {/* Header */}
      <Box
        component="header"
        sx={{
          bgcolor: '#fff',
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}
      >
        <Box
          sx={{
            maxWidth: 1400,
            mx: 'auto',
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 }
          }}
        >
          {/* Logo e título (customizável por tenant) */}
          <AppLogo />

          {isStagingEnv && (
            <Tooltip
              title="Você está acessando o ambiente de testes. Os dados aqui não afetam o ambiente de produção."
              placement="bottom-start"
              arrow
            >
              <Chip
                label="AMBIENTE TESTES"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  bgcolor: '#7b1fa2',
                  color: '#fff',
                  borderRadius: 1,
                  cursor: 'help',
                  '& .MuiChip-label': { px: 0.75 }
                }}
              />
            </Tooltip>
          )}

          {isDevEnv && (
            <Tooltip
              title="Você está acessando o ambiente de desenvolvimento local. Os dados aqui não afetam produção."
              placement="bottom-start"
              arrow
            >
              <Chip
                label="DESENVOLVIMENTO"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  bgcolor: '#e65100',
                  color: '#fff',
                  borderRadius: 1,
                  cursor: 'help',
                  '& .MuiChip-label': { px: 0.75 }
                }}
              />
            </Tooltip>
          )}

          {betaMode && (
            <Tooltip
              title="Você está usando a versão de avaliação desta ferramenta. Algumas funcionalidades têm limites temporários de uso."
              placement="bottom-start"
              arrow
            >
              <Chip
                label="BETA"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  bgcolor: 'warning.main',
                  color: '#fff',
                  borderRadius: 1,
                  cursor: 'help',
                  '& .MuiChip-label': { px: 0.75 }
                }}
              />
            </Tooltip>
          )}

          <Box sx={{ flex: 1 }} />

          {/* Ações */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={e => handleFilesSelected(e.target.files)}
              accept={getAcceptString()}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleUploadClick}
              disabled={isDisabled}
              sx={{
                borderRadius: 2,
                px: { xs: 1.5, sm: 2.5 },
                py: 0.75,
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                fontWeight: 500,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { boxShadow: '0 2px 8px rgba(25, 118, 210, 0.25)' }
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                Novo arquivo
              </Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                Novo
              </Box>
            </Button>

            <Tooltip title="Atualizar lista">
              <span>
                <IconButton
                  size="small"
                  onClick={() => fetchFiles(currentPage, sortOption)}
                  disabled={loading || isUploading}
                  sx={{
                    bgcolor: 'action.hover',
                    '&:hover': { bgcolor: 'action.selected' }
                  }}
                >
                  {loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>

            {emailTestEnabled && (
              <Tooltip title={testEmailAddress
                ? `[DEV] Enviar email de teste para: ${testEmailAddress}`
                : '[DEV] Email de teste (configure VITE_API_USER_EMAIL no .env.local)'}
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={async () => {
                      const email = testEmailAddress || window.prompt('Email de destino para o teste:') || ''
                      if (!email) return
                      setIsSendingTestEmail(true)
                      try {
                        const result = await sendTestEmail(email)
                        showMessage(result.sent
                          ? `✅ Email de teste enviado para ${result.email}`
                          : `❌ ${result.message}`
                        )
                      } catch (err) {
                        showMessage(`❌ Erro ao disparar email: ${err instanceof Error ? err.message : 'Erro desconhecido'}`)
                      } finally {
                        setIsSendingTestEmail(false)
                      }
                    }}
                    disabled={isSendingTestEmail}
                    sx={{
                      bgcolor: 'warning.light',
                      color: 'warning.dark',
                      border: '1px dashed',
                      borderColor: 'warning.main',
                      '&:hover': { bgcolor: 'warning.main', color: '#fff' },
                      '&:disabled': { opacity: 0.6 }
                    }}
                  >
                    {isSendingTestEmail
                      ? <CircularProgress size={18} color="warning" />
                      : <EmailIcon fontSize="small" />
                    }
                  </IconButton>
                </span>
              </Tooltip>
            )}

            <Tooltip title={userDisplayName || 'Informações do usuário'}>
              <IconButton
                size="small"
                onClick={() => setShowUserInfoModal(true)}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main', bgcolor: 'rgba(25, 118, 210, 0.08)' }
                }}
              >
                <AccountCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {isStandaloneAuth && (
              <Tooltip title="Sair">
                <IconButton
                  size="small"
                  onClick={handleLogout}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'error.main',
                      bgcolor: 'rgba(211, 47, 47, 0.08)'
                    }
                  }}
                >
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isDevEnv || isStagingEnv && (
              <UserInfoModal
                open={showUserInfoModal}
                onClose={() => setShowUserInfoModal(false)}
                displayName={userDisplayName}
                email={userEmail}
                userId={userId}
                tennantId={userTennantId}
                userTags={userTags}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box 
        ref={fileListContainerRef} 
        onMouseDown={handleContainerMouseDown} 
        onClick={handleContainerClick}
        sx={{ flex: 1 }}
      >
        <Box ref={selectionContainerRef} sx={{ maxWidth: 1400, mx: 'auto', px: 3, py: 3 }}>
          <DropZone
            onFilesDropped={handleFilesSelected}
            onInvalidFiles={msg => showMessage(msg)}
            disabled={isDisabled}
          >
            <SelectionBox
              containerRef={selectionContainerRef as React.RefObject<HTMLElement>}
              selectableSelector="[data-select-id]"
              idAttribute="data-select-id"
              onSelectionChange={handleRubberBandSelection}
              enabled={!loading && filteredFiles.length > 0}
            >
              <Stack spacing={2}>
            <Toolbar
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              sortOption={sortOption}
              onSortChange={handleSortChange}
              onAdvancedSearch={() => setShowSearchModal(true)}
            />

            <FolderBreadcrumb
              currentFolderId={currentFolderId}
              onNavigate={handleNavigateToFolder}
            />

            {/* Error display */}
            {error && files.length === 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: 6,
                  mt: 4,
                  border: '1px solid #ffcdd2',
                  bgcolor: '#fff5f5',
                  textAlign: 'center',
                  borderRadius: 3
                }}
              >
                <Box
                  sx={{
                    width: 96,
                    height: 96,
                    borderRadius: '50%',
                    bgcolor: error.type === 'network' ? '#ffebee' : '#fff3e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3
                  }}
                >
                  {error.type === 'network' ? (
                    <WifiOffIcon sx={{ fontSize: 48, color: '#f44336' }} />
                  ) : (
                    <CloudOffIcon sx={{ fontSize: 48, color: '#ff9800' }} />
                  )}
                </Box>

                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: '#333' }}>
                  {error.title}
                </Typography>
                <Typography variant="body1" sx={{ color: '#666', mb: 2, maxWidth: 500, mx: 'auto' }}>
                  {error.message}
                </Typography>
                {error.suggestion && (
                  <Typography variant="body2" sx={{ color: '#888', mb: 4, maxWidth: 450, mx: 'auto' }}>
                    {error.suggestion}
                  </Typography>
                )}

                {error.canRetry && (
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => fetchFiles(currentPage, sortOption)}
                    startIcon={<RefreshIcon />}
                    sx={{ px: 4, py: 1.5, borderRadius: 2, fontSize: '1rem' }}
                  >
                    Tentar novamente
                  </Button>
                )}

                {retryCountdown > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="body2" sx={{ color: '#888', mb: 1.5 }}>
                      Tentando novamente automaticamente em <strong>{retryCountdown}</strong> segundos...
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(retryCountdown / 10) * 100}
                      sx={{ maxWidth: 300, mx: 'auto', height: 6, borderRadius: 3 }}
                    />
                  </Box>
                )}
              </Paper>
            )}

            {error && files.length > 0 && (
              <Alert
                severity={error.type === 'network' || error.type === 'server' ? 'error' : 'warning'}
                onClose={clearError}
                action={
                  error.canRetry ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => { clearError(); fetchFiles(currentPage, sortOption); }}
                      startIcon={<RefreshIcon />}
                    >
                      Tentar novamente
                    </Button>
                  ) : undefined
                }
              >
                <AlertTitle>{error.title}</AlertTitle>
                {error.message}
              </Alert>
            )}

            {/* File list */}
            <FileList
              files={filteredFiles}
              viewMode={viewMode}
              loading={loading || isMovingItems}
              hasError={!!hasConnectionError}
              onOpen={handleOpenAnalysis}
              onDelete={handleDelete}
              onUploadClick={handleUploadClick}
              selectionMode={true}
              selectedFiles={selectedFiles}
              onSelectFile={toggleSelection}
              folders={folders}
              onFolderClick={handleNavigateToFolder}
              onFolderRename={handleRenameFolder}
              onFolderMove={(folder) => { openMoveDialog(folder) }}
              onFolderDelete={handleDeleteFolder}
              onFolderChangeColor={openEditDialog}
              onDropItemsToFolder={handleDropItemsToFolder}
              onMoveToFolder={handleMoveFileToFolder}
              onRename={handleRenameFile}
              insideFolder={!!currentFolderId}
              folderName={currentFolderName}
              onCreateFolder={openCreateDialog}
            />

            {/* Pagination */}
            {totalPages > 1 && !searchQuery && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
              </Stack>
            </SelectionBox>
          </DropZone>
        </Box>
      </Box>

      {/* FAB para upload (mobile) */}
      <Fab
        color="primary"
        onClick={handleUploadClick}
        disabled={isDisabled}
        sx={{
          position: 'fixed',
          bottom: selectedFiles.length > 0 ? 120 : 24,
          right: 24,
          display: { xs: 'flex', md: 'none' },
          transition: 'bottom 0.3s ease'
        }}
      >
        <AddIcon />
      </Fab>

      {/* Modals */}
      <FileViewer file={viewerFile} onClose={() => setViewerFile(null)} />

      <UploadConfirmationModal
        open={showConfirmation}
        files={pendingFiles}
        onClose={() => {
          cancelUpload()
          if (fileInputRef.current) fileInputRef.current.value = ''
        }}
        onRemoveFile={removePendingFile}
        onConfirm={handleConfirmUpload}
        userEmail={config?.userEmail}
      />

      <UploadProgress uploadingFiles={uploadingFiles} />

      <SelectionToolbar
        selectedFiles={selectedFiles}
        totalFiles={filteredFiles.length}
        onSelectAll={handleSelectAll}
        onClearSelection={clearSelection}
        onAction={handleBatchAction}
        onClose={clearSelection}
      />

      <CreateFolderDialog
        open={showCreateDialog}
        onClose={closeCreateDialog}
        onConfirm={handleCreateFolder}
      />

      <CreateFolderDialog
        open={!!editingFolder}
        onClose={closeEditDialog}
        onConfirm={handleConfirmRenameFolder}
        editMode
        initialName={editingFolder?.name}
        initialColor={editingFolder?.color}
        title="Editar pasta"
      />

      <MoveToFolderDialog
        open={showMoveDialog}
        onClose={() => { closeMoveDialog(); setMovingFile(null) }}
        onConfirm={handleConfirmMoveToFolder}
        excludeFolderIds={movingFolder ? [movingFolder.id] : []}
        currentFolderId={currentFolderId}
        itemDescription={
          movingFolder
            ? `Mover pasta "${movingFolder.name}"`
            : movingFile
              ? `Mover "${movingFile.displayName || movingFile.title}"`
              : selectedFiles.length > 0
                ? `${selectedFiles.length} arquivo(s) selecionado(s)`
                : undefined
        }
      />

      <MoveSuccessToast
        open={moveToast.open}
        message={moveToast.message}
        folderName={moveToast.folderName}
        folderColor={moveToast.folderColor}
        onClose={closeMoveToast}
      />

      {/* Search Modal */}
      <SearchModal
        open={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        files={files}
        folders={folders}
        onOpenFile={(fileId, segmentIndex) => {
          const file = files.find(f => f.fileId === fileId)
          if (file) {
            handleOpenAnalysis(file)
          }
        }}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog(prev => ({ ...prev, open: false }))}
        onConfirm={deleteDialog.onConfirm}
        itemType={deleteDialog.itemType}
        itemName={deleteDialog.itemName}
        items={deleteDialog.items}
        isDeleting={isDeleting}
      />

      {/* Rename Dialog */}
      <RenameDialog
        open={renameDialog.open}
        onClose={() => setRenameDialog({ open: false, file: null })}
        onConfirm={handleConfirmRenameFile}
        currentName={renameDialog.file?.displayName || renameDialog.file?.title || ''}
        originalName={renameDialog.file?.title || ''}
        isRenaming={isRenaming}
      />
    </Box>
  )
}

// ═══════════════════════════════════════════
// App com Providers e controle de autenticação
// ═══════════════════════════════════════════

export default function App({ config }: { config?: AppConfig }) {
  const [authConfig, setAuthConfig] = useState<AppConfig | undefined>(config)
  const [showLogin, setShowLogin] = useState(false)
  const [authChecked, setAuthChecked] = useState(false)

  useEffect(() => {
    // 1) WordPress or test env provided full auth
    if (config?.userId && config?.tennantId) {
      setAuthConfig(config)
      setAuthChecked(true)
      return
    }

    // 2) Check for stored session from previous standalone login
    const session = getSession()
    if (session) {
      applyAuthToConfig(session)
      setAuthConfig(window.monkeeTranscription as AppConfig)
      setAuthChecked(true)
      return
    }

    // 3) No auth available — show login if needed
    if (needsLogin()) {
      setShowLogin(true)
      setAuthChecked(true)
      return
    }

    // Fallback: config might be partially available (edge case)
    setAuthConfig(config)
    setAuthChecked(true)
  }, [config])

  const handleLoginSuccess = useCallback((authData: AuthData) => {
    applyAuthToConfig(authData)
    setAuthConfig(window.monkeeTranscription as AppConfig)
    setShowLogin(false)
  }, [])

  // Wait for auth check to complete
  if (!authChecked) {
    return null
  }

  // Show login page if no auth context is available
  if (showLogin) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} />
  }

  return (
    <AppProviders config={authConfig}>
      <AppContent config={authConfig} />
    </AppProviders>
  )
}
